// fields that are unique to Haven Connect and are re-used throughout the app
import React from "react";

import { gql } from "graphql-tag";
import { useQuery } from "@apollo/client";
import { SelectProps } from "@mui/material";
import { sortBy } from "lodash";
import { Navigate } from "react-router-dom";
import { getInterestsLists, orgsForForm, UserRole } from "types/gql-types";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { graphqlErrorIndicatesForbidden } from "utils/graphql-auth-error";
import { MaterialFormikSelect } from "./select";


// used with SelectRole to pluck out role string
export const getHighestRole = (roles: UserRole[]): UserRole | "" => {
  if (roles.includes(UserRole.ADMIN)) return UserRole.ADMIN;
  else if (roles.includes(UserRole.SUPERVISOR)) return UserRole.SUPERVISOR;
  else if (roles.includes(UserRole.PROPERTY_MANAGER)) return UserRole.PROPERTY_MANAGER;
  else if (roles.includes(UserRole.CLIENT)) return UserRole.CLIENT;
  return "";
};

type SelectRoleProps = { name: string; label: string, required?: boolean }
// display Select choice on select field
export const SelectRole: React.FC<SelectRoleProps> = (props) => (
  <MaterialFormikSelect title={props.label} margin="none" {...props}
    options={[
      { label: "Select", value: "" },
      { label: "Supervisor", value: UserRole.SUPERVISOR },
      { label: "Property Manager", value: UserRole.PROPERTY_MANAGER },
    ]}
  />
);

const GET_ORGS = gql`
  query orgsForForm {
    organizations {
      id
      name
    }
  }
`;

type SelectOrganizationProps = { name: string; label: string; showLabel?: boolean } & SelectProps

// render select filled with all organizations
export const SelectOrganization: React.FC<SelectOrganizationProps> = (props) => {
  const { loading, error, data } = useQuery<orgsForForm>(GET_ORGS, {
    fetchPolicy: "cache-and-network",
  });

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data || !data.organizations) {
    return <ServerError desc="Unable to fetch Organizations" />;
  }
  return <MaterialFormikSelect title={props.label} name={props.name}
    margin="none"
    options={[
      { label: "Select", value: "" },
      ...sortBy(data.organizations, (org) => org.name).map(({ id, name }) =>
        ({ label: name, value: id })),
    ]}
  />;
};

const GET_INTEREST_LISTS = gql`
  query getInterestsLists($isWaitList: Boolean!) {
    interestLists(isWaitlist: $isWaitList) {
      id
      name
    }
  }
`;


type SelectInterestListProps = { name: string; label: string; isWaitlist: boolean }
// render select filled with all organizations
export const SelectInterestList: React.FC<SelectInterestListProps> = (props) => {
  const { loading, error, data } = useQuery<getInterestsLists>(GET_INTEREST_LISTS, {
    variables: { isWaitList: props.isWaitlist },
    fetchPolicy: "cache-and-network",
  });
  if (error && graphqlErrorIndicatesForbidden(error)) {
    return <Navigate to="/not-authorized" replace />;
  }

  if (error) return <ServerError desc={error.message} />;
  if (loading || !data) return <LoadingSpinner />;
  return (<MaterialFormikSelect
    name={props.name}
    title={props.label}
    margin="none"
    options={[
      { label: "Select", value: "" },
      ...data.interestLists.slice().sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      }).map(({ id, name }) => ({ label: name, value: id })),
    ]}/>);
};
